import PropTypes from "prop-types";
import classNames from "classnames";
import { AwsRum } from "aws-rum-web";
import { useState, useEffect, useMemo, useCallback } from "react";
import { BrowserRouter } from "react-router-dom";
import { ThemeProvider } from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { ThemeProvider as BTThemeProvider } from "react-bootstrap";
import { useBottomScrollListener } from "react-bottom-scroll-listener";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import {
  toast,
  setScrollRefBottom,
  setScrollTopValue,
  setCollapsedSidebar,
} from "./slice";
import {
  ENV,
  ASIDE_STATE_KEY,
  AWS_APP_IDENTITY_POOL,
  AWS_GUEST_ROLE_ARN,
  AWS_RUM_KEYS,
  AWS_RUM_URL,
  THEME_TYPES,
  PURCHASE_PLAN_MODAL_KEY,
  WELCOME_MODAL_KEY,
  GOOGLE_AUTH_ID,
  USER_WALKTHROUGH_FLAGS,
  SUGGESTED_STRATEGY_MODAL_KEY,
  LAST_VISIT_KEY,
} from "utils/constants";
import {
  FORGOT_PASS_PATH,
  RESET_PASS_PATH,
  SIGNIN_PATH,
  CHECK_EMAIL_PATH,
  SIGNUP_PATH,
  VERIFY_PHONE_PATH,
  WELCOME_PATH,
  SIGNUP_VERIFY_EMAIL_PATH,
  WELCOME_QUESTIONS_PATH,
  PASSWORD_RESET_SUCCESS_PATH,
  PASSWORD_RESET_COMPLETE_PATH,
} from "features/Auth/routes";
import {
  showPurchasePlanModalSelect,
  showSuggestedStrategyModalSelect,
  lockModalSelect,
  showUnsupportedBankModalSelect,
  themeState,
  userDetailSelect,
  walkthroughStateSelect,
  showWelcomeModalSelect,
} from "Layout/slice/selector";
import { showStatusModalSelect } from "features/Dashboard/slice/selector";
import {
  lightTheme as lightThemeV2,
  darkTheme as darkThemeV2,
} from "style/v2/theme";
import {
  setWelcomeWalkthrough,
  toggleGetStartedPanel,
  toggleSuggestedstrategyModal,
  setLockModal,
  toggleUnsupportedBankModal,
} from "./slice/index";

import Banner from "shared/Banner";
import GlobalStyle from "style/global";
import { Toast } from "components/Toast";
import { Sidebar } from "components/Sidebar";
import OverrideStyle from "style/override";
import { Navbar } from "components/Navbar";
import Footer from "components/Footer";
import { setTrackingUserId, trackEvent } from "utils/tracking";
import LocationPath from "hooks/locationPath";
import { SettingsReturnTracker } from "features/Account/v2/SettingsModal/SettingsReturnTracker";
import ScrollButton from "shared/ScrollButton";
import { fetchStocks, fetchUserDetails, fetchUserFlags } from "./slice/thunk";
import {
  getItemFromStorage,
  setItemInStorage,
  getItemPersist,
  setItemPersist,
} from "utils/storage";
import { LayoutContainer, LayoutContent } from "./Layout.styles";
import { PLANS_PATH } from "features/Plans/routes";
import { toastDataSelect } from "./slice/selector";
import { UpgradePremiumModal } from "shared";
import { contains, isLoggedIn, toTitleCase, isFreeUser } from "utils";
// import ManualTradingFloatingButton from "shared/ManualTradingFloatingButton";
import { PremiumPlanModal } from "shared/Modal/PremiumPlanModal";
// import { PurchasePlanModal } from "shared/Modal/PurchasePlanModal";
import { togglePurchasePlanModal, toggleWelcomeModal } from "./slice";
import { WelcomeModal } from "shared/InAppOnboardingGuide/WelcomeModal";
import { Walkthrough } from "shared/InAppOnboardingGuide/Walkthrough";
import { useIdleTimer } from "hooks/useIdleTimer";
import { setStrategyType } from "features/Strategy/slice/index";
import { SuggestedStrategyModal } from "shared/Modal/SuggestedStrategiesModal/index";
// import { GetStartedFloatingButton } from "shared/InAppOnboardingGuide/GetStartedFloatingButton";
import LockModal from "shared/Modal/LockModal";
import UnsupportedBankModal from "shared/Modal/UnsupportedBankModal";
import { RESEARCH_PATH } from "features/Research/routes";
import { MAIN_ACCOUNT_PATH } from "features/Account/routes";
import { ChoosePlanModal } from "features/Account/v2/ChoosePlanModal/index";
import { STRATEGY_V2_PATH } from "features/Strategy/routes";

if (ENV === "production") {
  try {
    // Initialize RUM
    let awsRum = null;
    const config = {
      sessionSampleRate: 1,
      guestRoleArn: AWS_GUEST_ROLE_ARN,
      identityPoolId: AWS_APP_IDENTITY_POOL,
      endpoint: AWS_RUM_URL,
      telemetries: ["performance", "errors", "http"],
      allowCookies: true,
      enableXRay: false,
    };

    awsRum = new AwsRum(
      AWS_RUM_KEYS.APPLICATION_ID,
      AWS_RUM_KEYS.APPLICATION_VERSION,
      AWS_RUM_KEYS.APPLICATION_REGION,
      config
    );
    console.log(awsRum.tracking_id);
  } catch (error) {
    console.log(error);
  }
}
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: 1,
    },
  },
});

export const Layout = ({ children }) => {
  const dispatch = useDispatch();
  const theme = useSelector(themeState);
  const toastData = useSelector(toastDataSelect);
  const showPurchasePlanModal = useSelector(showPurchasePlanModalSelect);
  const lockModal = useSelector(lockModalSelect);
  const showUnsupportedBankModal = useSelector(showUnsupportedBankModalSelect);
  const userDetails = useSelector(userDetailSelect);
  const userFlags = useSelector(walkthroughStateSelect);
  const showSuggestedStrategyModal = useSelector(
    showSuggestedStrategyModalSelect
  );
  const visibleWelcomeModal = useSelector(showWelcomeModalSelect);
  const showStatusModal = useSelector(showStatusModalSelect);

  const [siteTitle, setSiteTitle] = useState("");
  const [isAuthPage, setIsAuthPage] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [isResearchPage, setIsResearchPage] = useState(false);

  const scrollRef = useBottomScrollListener(onBottom, {
    offset: 300,
  });

  // Init idle timer
  useIdleTimer();

  useEffect(() => {
    if (isLoggedIn()) {
      dispatch(fetchUserDetails());
      dispatch(fetchStocks());
      dispatch(fetchUserFlags());
      dispatch(setStrategyType(null));
    }
  }, []);

  useEffect(() => {
    const visibleWelcomeModal = getItemFromStorage(WELCOME_MODAL_KEY);
    const isOnPlansPage = window.location.pathname.includes("/plans");
    dispatch(toggleSuggestedstrategyModal(false));
    dispatch(setWelcomeWalkthrough(false));
    if (
      userDetails?.user_guide_shown &&
      !isAuthPage &&
      !visibleWelcomeModal &&
      !isOnPlansPage
    ) {
      dispatch(toggleWelcomeModal(true));
    }

    // Pass Unique User ID to Google Analytics
    if (
      window.location.hostname === "app.surmount.ai" &&
      userDetails?.tracking_id
    ) {
      setTrackingUserId(userDetails.tracking_id);

      // Track return visits
      const now = Date.now();
      const lastVisit = getItemPersist(LAST_VISIT_KEY);
      const MIN_GAP_MS = 30 * 60 * 1000; // 30 minutes

      if (lastVisit && now - Number(lastVisit) > MIN_GAP_MS) {
        trackEvent({
          name: "return_visit",
          label: "Engagement",
          days_since_last: Math.floor((now - Number(lastVisit)) / 86400000),
        });
      }

      setItemPersist(LAST_VISIT_KEY, now);
    }
  }, [userDetails, isAuthPage]);

  // Handle walkthrough state persist cases
  useEffect(() => {
    if (!isLoggedIn()) return;

    const wtSteps = Object.keys(USER_WALKTHROUGH_FLAGS);
    const hasWtSteps = userFlags?.filter(
      (uf) => wtSteps.includes(uf.flag) && uf.is_active === true
    );

    if (wtSteps?.length === hasWtSteps?.length) {
      dispatch(toggleGetStartedPanel(false));
    } else {
      dispatch(toggleGetStartedPanel(true));
    }
  }, [userFlags]);

  const changedPath = useCallback(
    (pathname) => {
      handleScrollTop();
      handleHideLockModal();

      // Set tab title
      let title = pathname.split("/")[1];
      if (contains(title, "signin")) {
        title = "";
      } else if (title) {
        title = title.replace("-", " ");
        title = toTitleCase(title);
      } else {
        title = "Dashboard";
      }
      setSiteTitle(title);
      dispatch(setCollapsedSidebar(false));

      // Set aside state
      if (
        (contains(pathname, "/strategy") ||
          contains(pathname, STRATEGY_V2_PATH)) &&
        contains(pathname, "/edit")
      ) {
        setItemInStorage(ASIDE_STATE_KEY, String(true));
      }

      // Set auth page visibility
      if (
        contains(pathname, SIGNUP_PATH) ||
        contains(pathname, SIGNIN_PATH) ||
        contains(pathname, FORGOT_PASS_PATH) ||
        contains(pathname, RESET_PASS_PATH) ||
        contains(pathname, CHECK_EMAIL_PATH) ||
        contains(pathname, VERIFY_PHONE_PATH) ||
        contains(pathname, SIGNUP_VERIFY_EMAIL_PATH) ||
        contains(pathname, WELCOME_PATH) ||
        contains(pathname, WELCOME_QUESTIONS_PATH) ||
        contains(pathname, PLANS_PATH) ||
        contains(pathname, PASSWORD_RESET_SUCCESS_PATH) ||
        contains(pathname, PASSWORD_RESET_COMPLETE_PATH)
      ) {
        setIsAuthPage(true);
      } else {
        setIsAuthPage(false);
      }

      if (
        contains(pathname, PLANS_PATH) ||
        contains(pathname, MAIN_ACCOUNT_PATH)
      ) {
        setShowSidebar(false);
      } else {
        setShowSidebar(true);
      }
      if (contains(pathname, RESEARCH_PATH)) {
        setIsResearchPage(true);
      } else {
        setIsResearchPage(false);
      }

      const closedPurchaseModal = getItemFromStorage(PURCHASE_PLAN_MODAL_KEY);

      if (
        isLoggedIn() &&
        !contains(pathname, WELCOME_PATH) &&
        !contains(pathname, SIGNUP_PATH) &&
        !contains(pathname, PLANS_PATH) &&
        !closedPurchaseModal &&
        isFreeUser()
      ) {
        dispatch(togglePurchasePlanModal(true));
        setItemInStorage(PURCHASE_PLAN_MODAL_KEY, true);
      } else {
        dispatch(togglePurchasePlanModal(false));
      }
    },
    [dispatch]
  );

  function onBottom() {
    dispatch(setScrollRefBottom(true));
  }

  const hideToast = () => {
    dispatch(
      toast({
        body: "",
        visible: false,
        type: toastData.type,
      })
    );
  };

  const handleScroll = (event) => {
    dispatch(setScrollTopValue(event.currentTarget.scrollTop));
  };

  const handleScrollTop = () => {
    document.getElementById("content")?.scrollTo(0, 0);
  };

  const handleHidePurchasePlan = () => {
    setItemInStorage(PURCHASE_PLAN_MODAL_KEY, true);
    dispatch(togglePurchasePlanModal(false));
  };

  const handleHideSuggestedStrategyModal = () => {
    setItemInStorage(SUGGESTED_STRATEGY_MODAL_KEY, true);
    dispatch(toggleSuggestedstrategyModal(false));
  };

  const handleHideUnsupportedBankModal = () => {
    dispatch(toggleUnsupportedBankModal(false));
  };

  const handleHideLockModal = () => {
    dispatch(
      setLockModal({
        visible: false,
        title: "",
        publicText: "",
        privateText: "",
      })
    );
  };

  const content = (
    <LayoutContent
      className={classNames("content", {
        "full-height p-0": isAuthPage,
        "px-0": isResearchPage,
        "no-sidebar": !isAuthPage && !showSidebar,
      })}
      ref={scrollRef}
      onScroll={handleScroll}
      id="content"
    >
      <div className="layout-body">{children}</div>
      <Footer />
    </LayoutContent>
  );

  const themeConfig = useMemo(() => {
    const isDarkTheme = theme === THEME_TYPES.DARK;

    return isDarkTheme ? darkThemeV2 : lightThemeV2;
  }, [theme]);

  return (
    <DndProvider backend={HTML5Backend}>
      <HelmetProvider>
        <ThemeProvider theme={themeConfig}>
          <GlobalStyle />
          <OverrideStyle />
          <BrowserRouter>
            <Helmet>
              <title className="text-capitalize">
                {siteTitle ? `${siteTitle} | Surmount AI` : "Surmount AI"}{" "}
              </title>
            </Helmet>
            <LocationPath changedPath={changedPath} />
            <SettingsReturnTracker />
            <QueryClientProvider client={queryClient}>
              <BTThemeProvider prefixes={{}}>
                <GoogleOAuthProvider clientId={GOOGLE_AUTH_ID}>
                  <LayoutContainer>
                    <Banner />
                    <div
                      className={classNames("app", {
                        "auth-app": isAuthPage,
                      })}
                    >
                      <div className="app-content">
                        {!isAuthPage && <Navbar />}

                        <Sidebar />

                        <div
                          className={classNames("screen", {
                            "mx-0 w-100":
                              isAuthPage || !showSidebar || !isLoggedIn(),
                            "pt-0": isAuthPage,
                          })}
                        >
                          {/* Dynamic View */}
                          {content}

                          <Toast
                            onClose={hideToast}
                            type={toastData?.type}
                            body={toastData?.body}
                            show={toastData?.visible}
                            header={toastData?.header}
                            delay={toastData?.delay}
                          />
                        </div>
                      </div>
                    </div>

                    <ScrollButton handleScrollTop={handleScrollTop} />

                    {/* {ENV === "development" && <ManualTradingFloatingButton />} */}

                    {/* <GetStartedFloatingButton /> */}
                  </LayoutContainer>
                </GoogleOAuthProvider>
              </BTThemeProvider>

              <UpgradePremiumModal />

              <PremiumPlanModal />

              {/* <PurchasePlanModal
                show={showPurchasePlanModal}
                onHide={handleHidePurchasePlan}
              /> */}
              <ChoosePlanModal
                show={showPurchasePlanModal}
                onHide={handleHidePurchasePlan}
              />

              {!showStatusModal && visibleWelcomeModal && <WelcomeModal />}

              <Walkthrough />

              {showSuggestedStrategyModal && (
                <SuggestedStrategyModal
                  show={showSuggestedStrategyModal}
                  onHide={handleHideSuggestedStrategyModal}
                />
              )}

              <UnsupportedBankModal
                visible={showUnsupportedBankModal}
                onHide={handleHideUnsupportedBankModal}
              />

              <LockModal
                visible={lockModal.visible}
                isPublicPage={false}
                title={lockModal.title}
                publicText={lockModal.publicText}
                privateText={lockModal.privateText}
                onHide={handleHideLockModal}
              />
            </QueryClientProvider>
          </BrowserRouter>
        </ThemeProvider>
      </HelmetProvider>
    </DndProvider>
  );
};

Layout.propTypes = {
  children: PropTypes.node,
};
